import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { AdminService, Relative } from '../../services/admin.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, RouterModule],
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.css']
})
export class ProfileComponent implements OnInit {
  profileForm!: FormGroup;
  loading = false;
  saving = false;
  error: string | null = null;
  success: string | null = null;
  avatarUploading = false;
  avatarPreviewUrl: string | null = null;
  profileData: any = null;
  
  // Referral links
  parentReferralLink: string = '';
  spouseReferralLink: string = '';
  siblingReferralLink: string = '';
  baseUrl: string = '';

  constructor(
    private fb: FormBuilder,
    private adminService: AdminService
  ) {}

  ngOnInit(): void {
    // Get base URL for referral links
    this.baseUrl = window.location.origin;
    
    this.profileForm = this.fb.group({
      referralCode: [{ value: '', disabled: true }],
      parentReferralCode: [{ value: '', disabled: true }],
      spouseReferralCode: [{ value: '', disabled: true }],
      firstName: ['', Validators.required],
      middleName: [''],
      lastName: ['', Validators.required],
      email: ['', [this.optionalEmailValidator]],
      mobileNumber: [''],
      dateOfBirth: ['', Validators.required],
      gender: ['', Validators.required],
      qualification: [''],
      profession: [''],
      location: [''],
      username: ['', Validators.required],
      isDeceased: [false],
      password: ['']
    });

    this.loadProfile();
  }

  optionalEmailValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value || value.trim() === '') {
      return null; // Allow empty values
    }
    // If value is provided, validate email format
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(value) ? null : { email: true };
  }

  loadProfile(): void {
    this.loading = true;
    this.error = null;
    
    this.adminService.getMyProfile().subscribe({
      next: (resp: any) => {
        const data: Relative = resp.data;
        this.profileData = data;
        
        // Generate referral links
        this.generateReferralLinks(data);
        
        // Set profile image URL if available
        if (data.profileImageUrl) {
          // Ensure the URL is properly formatted
          let imageUrl = data.profileImageUrl;
          
          // If it's a relative path, make it absolute
          if (imageUrl.startsWith('/')) {
            imageUrl = `${environment.apiUrl}${imageUrl}`;
          }
          // If it's already a full URL, use it as is
          else if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
            // Use as is
          }
          // If it doesn't start with anything, assume it's a relative path
          else {
            imageUrl = `${environment.apiUrl}/${imageUrl}`;
          }
          
          this.avatarPreviewUrl = imageUrl;
          
          // Test if the image is accessible with fallback options
          this.testImageUrlWithFallback(imageUrl, data.profileImageUrl);
        } else {
          this.avatarPreviewUrl = null;
        }
        
        this.profileForm.patchValue({
          referralCode: data.referralCode,
          parentReferralCode: data.parentReferralCode,
          spouseReferralCode: (data as any).spouseReferralCode,
          firstName: data.firstName,
          middleName: data.middleName,
          lastName: data.lastName,
          email: data.email,
          mobileNumber: data.mobileNumber,
          dateOfBirth: data.dateOfBirth?.substring(0, 10),
          gender: data.gender,
          qualification: data.qualification,
          profession: data.profession,
          location: (data as any).location,
          username: data.username,
          isDeceased: data.isDeceased
        });
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Failed to load profile';
        this.loading = false;
      }
    });
  }

  saveProfile(): void {
    // Custom validation: at least one of email or mobile number must be provided
    const email = this.profileForm.get('email')?.value?.trim();
    const mobileNumber = this.profileForm.get('mobileNumber')?.value?.trim();
    
    if (!email && !mobileNumber) {
      this.error = 'Please provide at least an email address or mobile number';
      this.profileForm.get('email')?.markAsTouched();
      this.profileForm.get('mobileNumber')?.markAsTouched();
      return;
    }

    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.error = null;
    this.success = null;
    const value = this.profileForm.value;
    const payload: Partial<Relative> & { password?: string } = {
      firstName: value.firstName,
      middleName: value.middleName,
      lastName: value.lastName,
      email: value.email && value.email.trim() !== '' ? value.email : null,
      mobileNumber: value.mobileNumber && value.mobileNumber.trim() !== '' ? value.mobileNumber.trim() : null,
      dateOfBirth: value.dateOfBirth,
      gender: value.gender,
      qualification: value.qualification,
      profession: value.profession,
      location: value.location,
      username: value.username,
      isDeceased: value.isDeceased,
      password: value.password || undefined
    };

    this.adminService.updateMyProfile(payload).subscribe({
      next: () => {
        this.success = 'Profile updated successfully';
        this.saving = false;
        // Clear password field after successful save
        this.profileForm.get('password')?.reset('');
      },
      error: (err) => {
        this.error = 'Failed to update profile';
        console.error(err);
        this.saving = false;
      }
    });
  }

  onAvatarSelected(event: any): void {
    const file: File | undefined = event?.target?.files?.[0];
    if (!file) { return; }
    this.avatarUploading = true;
    this.error = null;
    this.success = null;
    this.adminService.uploadMyProfileImage(file).subscribe({
      next: (resp: any) => {
        const url = resp?.data || null;
        
        if (url) {
          // Process the URL the same way as in loadProfile
          let imageUrl = url;
          
          // If it's a relative path, make it absolute
          if (imageUrl.startsWith('/')) {
            imageUrl = `${environment.apiUrl}${imageUrl}`;
          }
          // If it's already a full URL, use it as is
          else if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
            // Use as is
          }
          // If it doesn't start with anything, assume it's a relative path
          else {
            imageUrl = `${environment.apiUrl}/${imageUrl}`;
          }
          
          this.avatarPreviewUrl = imageUrl;
          this.success = 'Profile image updated';
        } else {
          this.error = 'No image URL returned from server';
        }
        this.avatarUploading = false;
      },
      error: (err) => {
        this.error = 'Failed to upload image';
        this.avatarUploading = false;
      }
    });
  }

  onImageError(event: any): void {
    this.avatarPreviewUrl = null;
    this.error = 'Failed to load profile image. Please try uploading a new image.';
  }

  testImageUrlWithFallback(primaryUrl: string, originalUrl: string): void {
    const testUrl = (url: string, attempt: number) => {
      const img = new Image();
      
      img.onload = () => {
        this.avatarPreviewUrl = url;
        this.error = null;
      };
      
      img.onerror = (error) => {
        // Try next fallback URL
        if (attempt === 1) {
          // Try the original URL as-is
          testUrl(originalUrl, 2);
        } else if (attempt === 2) {
          // Try with different API URL format
          const alternativeUrl = originalUrl.startsWith('/') 
            ? `https://localhost:44304${originalUrl}`
            : `https://localhost:44304/${originalUrl}`;
          testUrl(alternativeUrl, 3);
        } else if (attempt === 3) {
          // Try with HTTP instead of HTTPS
          const httpUrl = primaryUrl.replace('https://', 'http://');
          testUrl(httpUrl, 4);
        } else {
          // All attempts failed
          this.avatarPreviewUrl = null;
          this.error = 'Profile image not accessible. Please upload a new image.';
        }
      };
      
      // Set a timeout for each attempt
      setTimeout(() => {
        if (!img.complete) {
          // Trigger the error handler manually
          const errorEvent = new Event('error');
          img.dispatchEvent(errorEvent);
        }
      }, 5000);
      
      img.src = url;
    };
    
    // Start with the primary processed URL
    testUrl(primaryUrl, 1);
  }

  testImageUrl(url: string): void {
    const img = new Image();
    
    img.onload = () => {
      // Image loaded successfully
    };
    
    img.onerror = (error) => {
      this.avatarPreviewUrl = null;
      this.error = 'Profile image not accessible. Please upload a new image.';
    };
    
    // Set a timeout to catch cases where the image never loads or errors
    setTimeout(() => {
      if (!img.complete) {
        // Trigger the error handler manually
        const errorEvent = new Event('error');
        img.dispatchEvent(errorEvent);
      }
    }, 10000);
    
    img.src = url;
  }

  generateReferralLinks(data: any): void {
    // Parent referral link - for adding children (uses own referral code)
    if (data.referralCode) {
      this.parentReferralLink = `${this.baseUrl}/signup?parentReferralCode=${data.referralCode}&parentName=${encodeURIComponent(data.firstName + ' ' + (data.lastName || ''))}`;
    }

    // Spouse referral link - for adding spouse (uses own referral code)
    if (data.referralCode) {
      this.spouseReferralLink = `${this.baseUrl}/signup?spouseReferralCode=${data.referralCode}&spouseName=${encodeURIComponent(data.firstName + ' ' + (data.lastName || ''))}`;
    }

    // Sibling referral link - for adding siblings (uses parent's referral code)
    // Note: For siblings, we use the parent's referral code but don't include parent name
    // since siblings share the same parent
    if (data.parentReferralCode) {
      this.siblingReferralLink = `${this.baseUrl}/signup?parentReferralCode=${data.parentReferralCode}`;
    } else {
      this.siblingReferralLink = ''; // No parent, so no sibling link available
    }
  }

  copyToClipboard(text: string, type: string): void {
    if (!text) {
      this.error = `${type} referral link is not available`;
      return;
    }
    
    navigator.clipboard.writeText(text).then(() => {
      this.success = `${type} referral link copied to clipboard!`;
      setTimeout(() => {
        this.success = null;
      }, 3000);
    }).catch(err => {
      this.error = 'Failed to copy link. Please copy manually.';
      console.error('Failed to copy:', err);
    });
  }

  shareLink(type: string): void {
    let link = '';
    let title = '';
    let text = '';

    switch (type) {
      case 'parent':
        link = this.parentReferralLink;
        title = 'Add me as your parent on Familitive';
        text = `Join my family tree on Familitive! Use this link to register and connect with me: ${link}`;
        break;
      case 'spouse':
        link = this.spouseReferralLink;
        title = 'Join me on Familitive as my spouse';
        text = `Join my family tree on Familitive! Use this link to register and connect as my spouse: ${link}`;
        break;
      case 'sibling':
        link = this.siblingReferralLink;
        title = 'Join me as my sibling on Familitive';
        text = `Join my family tree on Familitive! Use this link to register and connect as my sibling: ${link}`;
        break;
    }

    if (!link) {
      this.error = `${type} referral link is not available`;
      return;
    }

    // Use Web Share API if available, otherwise fallback to clipboard
    if (navigator.share) {
      navigator.share({
        title: title,
        text: text,
        url: link
      }).then(() => {
        this.success = `Shared ${type} referral link successfully!`;
        setTimeout(() => {
          this.success = null;
        }, 3000);
      }).catch(err => {
        // User cancelled or error - fallback to copy
        this.copyToClipboard(link, type);
      });
    } else {
      this.copyToClipboard(link, type);
    }
  }
}


